<script setup lang="ts">
import { computed, watch } from 'vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import { Button } from '@/components/ui/button';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import type { BulkLocationStep, Event, MultiStepFormState } from '@/types';

/*
 * Creates a bulk registration: one registration on a barn, divided into a number of the barn's
 * sections in the form - the result being an ordinary registration per section. Offered only to
 * integrator directories, and only where registrations are made on sections. The API decides the
 * rest: it only offers the barns whose sections take the registration, and refuses bulk to anyone
 * but an integrator.
 */
const props = withDefaults(
    defineProps<{
        /** The form of the registration, e.g. 1 for insertions */
        formId: number;
        label: string;
        /** How the location is chosen - see MultiStepFormState.bulk_location_step */
        locationStep?: BulkLocationStep;
    }>(),
    { locationStep: 'barnWithSections' }
);

const emit = defineEmits<{ (e: 'form-activity'): void }>();

// What the button needs is only loaded for those who may see it
watch(
    () => store.getters.isIntegratorDirectory,
    (isIntegratorDirectory) => {
        if (isIntegratorDirectory) {
            store.dispatch(ActionType.GetActions);
            store.dispatch(ActionType.GetEvents);
            store.dispatch(ActionType.GetSections);
        }
    },
    { immediate: true }
);

const action = computed(() => store.state.actions.find((currentAction) => currentAction.form_id === props.formId));

const isVisible = computed(() => {
    const actionId = action.value?.id;
    return store.getters.isIntegratorDirectory && actionId !== undefined && (store.state.sections?.some((section) => section.accessible_actions?.includes(actionId)) ?? false);
});

const initialState = computed<MultiStepFormState>(() => {
    // The icon of the ordinary registration heads the form
    const event = store.state.events.find((currentEvent) => currentEvent.action_id === action.value?.id);

    return {
        location: null,
        form: null,
        confirmed: false,
        farm: null,
        record_id: null,
        notification: null,
        event: action.value ? ({ id: event?.id ?? action.value.id, name: props.label, action_id: action.value.id, icon_url: event?.icon_url ?? '' } as Event) : null,
        bulk: true,
        bulk_location_step: props.locationStep,
    };
});
</script>

<template>
    <ActionModal v-if="isVisible" :initial-step-index="0" :initial-state="initialState" @closing="emit('form-activity')">
        <template #default="{ open }">
            <Button size="sm" @click="open()">+ {{ label }}</Button>
        </template>
    </ActionModal>
</template>
